/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useCallback, useEffect, useMemo, useState, type ErrorInfo, type MouseEvent } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { IntlProvider, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { cssMap, jsx } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import Modal from '@atlaskit/modal-dialog/modal-dialog';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import type { KeyboardOrMouseEvent } from '@atlaskit/modal-dialog/types';
import { Box } from '@atlaskit/primitives/compiled/box';
import { token } from '@atlaskit/tokens';
import { captureException } from '@atlassian/error-handling/sentry';
import { openLoomMeetingRecordings } from '@atlassian/loom-meeting-recordings';

import { SOURCE_COMPONENT, SOURCE_CONTEXT } from '../../constants';
import type { Edition, PrefetchedPlanData } from '../../services/modal-data/types';
import { NormalizeSeatStartError, normalizeSeat } from '../../services/user-access';
import type { ScreenState } from './common/types';
import {
	type ModalContextValue,
	ModalProvider,
	useModalContext,
	useModalState,
} from './contexts/modal-context';
import { ConfirmationScreen } from './screens/confirmation-screen';
import { ConfirmationScreenLoom } from './screens/confirmation-screen-loom';
import { messages as confirmationMessagesLoom } from './screens/confirmation-screen-loom/messages';
import { messages as confirmationMessages } from './screens/confirmation-screen/messages';
import { ErrorScreen } from './screens/error-screen';
import { LoadingScreen } from './screens/loading-screen';
import { NormalizeSeatScreen } from './screens/normalize-seat-screen';
import { ProvisioningScreen } from './screens/provisioning-screen';
import { RequestedTrialScreen } from './screens/requested-trial-screen';
import { TimeoutScreen } from './screens/timeout-screen';
import { WelcomeScreen } from './screens/welcome-screen';
import { WelcomeScreenTwcgAnimation } from './screens/welcome-screen-twcg-animation';
import { useMessages } from './utils/i18n';
import { hasRequestedTrial, saveRequestedTrialToLocalStorage } from './utils/local-storage';
import { type ParsedOnboardingUrl, parseOnboardingUrl } from './utils/parse-onboarding-url';
import { sendRequestTrial } from './utils/send-request-trial';

const styles = cssMap({
	modalWrapper: {
		position: 'relative',
		width: '100%',
		overflow: 'hidden',
		maxWidth: '968px',
		borderRadius: token('radius.small'),
		minHeight: '568px',
	},
});

export type ProvisioningResult = {
	parsedUrl: ParsedOnboardingUrl;
	rawUrl: string;
	success: boolean;
};

export type WelcomeScreenId = 'welcome' | 'welcome-screen-twcg-animation';

type Props = {
	autoStart?: boolean;
	cloudId: string;
	/** Overrides the first screen rendered by the modal, primarily for development fixtures. */
	initialScreen?: ScreenState;
	locale?: string;
	onClose: () => void;
	onComplete?: (result: ProvisioningResult) => void;
	/** Selects an optional welcome-screen variant after successful provisioning. */
	onGetWelcomeScreen?: () => WelcomeScreenId;
	onRequestSubmitted?: (result: { approvalUrl?: string }) => void;
	/** Evaluated after provisioning to skip welcome and continue to seat normalization. */
	onShouldSkipWelcomeScreen?: () => boolean;
	onTryNowClick?: () => void;
	/** Called after the TWC Graph welcome animation completes. */
	onWelcomeScreenTwcgAnimationComplete?: (result: ProvisioningResult) => void;
	orgId?: string;
	prefetch?: PrefetchedPlanData;
	/** Requests a specific TWC edition during planning and provisioning. */
	requestedEditionId?: Edition;
	/** Skips the nth-user seat normalization screen after provisioning. */
	skipNthUserScreen?: boolean;
	sourceComponent: (typeof SOURCE_COMPONENT)[keyof typeof SOURCE_COMPONENT];
	sourceContext: (typeof SOURCE_CONTEXT)[keyof typeof SOURCE_CONTEXT];
	testId?: string;
	touchpointName?: string;
	upgradeSourceProduct?: 'jira' | 'confluence';
	/** Sets the welcome-screen variant when opening directly on an existing welcome screen. */
	welcomeScreen?: WelcomeScreenId;
};

export type TwcXflowModalProps = Props;

type InternalModalProps = {
	onClose: () => void;
	onComplete?: (result: ProvisioningResult) => void;
	onGetWelcomeScreen?: () => WelcomeScreenId;
	onRequestSubmitted?: (result: { approvalUrl?: string }) => void;
	onShouldSkipWelcomeScreen?: () => boolean;
	onTryNowClick?: () => void;
	onWelcomeScreenTwcgAnimationComplete?: (result: ProvisioningResult) => void;
	skipNthUserScreen?: boolean;
	testId?: string;
	welcomeScreen?: WelcomeScreenId;
};

const isMeetingRecordingsFlow = (sourceComponent: string, sourceContext: string): boolean => {
	if (sourceComponent === SOURCE_COMPONENT.TWC_XFLOW_CONFLUENCE_TEMPLATE) {
		return (
			sourceContext === SOURCE_CONTEXT.CONFLUENCE_XFLOW_SIDENAV ||
			sourceContext === SOURCE_CONTEXT.CONFLUENCE_XFLOW_FLAG
		);
	}

	return (
		sourceComponent === SOURCE_COMPONENT.CONFLUENCE_HOME &&
		sourceContext === SOURCE_CONTEXT.CONFLUENCE_MEETINGS_TAB
	);
};

const isNormalizeSeatModalEnabled = (): boolean =>
	FeatureGates.getExperimentValue<boolean>(
		'twc_normalize_seat_modal_gate_v2_config',
		'value',
		false,
	);

const TwcXflowModal = ({
	onClose,
	onComplete,
	onGetWelcomeScreen,
	onWelcomeScreenTwcgAnimationComplete,
	onRequestSubmitted,
	onShouldSkipWelcomeScreen,
	onTryNowClick,
	skipNthUserScreen = false,
	testId,
	welcomeScreen: initialWelcomeScreen = 'welcome',
}: InternalModalProps): JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const {
		cloudId,
		sourceComponent,
		sourceContext,
		currentScreen,
		setCurrentScreen,
		setErrorMessage,
		modalState,
		redirectUrl,
		setRedirectUrl,
		isNormalizeSeatsEnabled,
	} = useModalContext();

	const [approvalUrl, setApprovalUrl] = useState<string | undefined>(undefined);
	const [welcomeScreen, setWelcomeScreen] = useState<WelcomeScreenId>(initialWelcomeScreen);
	const showMeetingRecordingsCta = isMeetingRecordingsFlow(sourceComponent, sourceContext);

	const canActivate = useMemo(
		() => modalState.activationCapability === 'can-activate',
		[modalState.activationCapability],
	);

	useEffect(() => {
		if (!cloudId) {
			setErrorMessage('Missing cloudId');
			setCurrentScreen('error');
		}
	}, [cloudId, setCurrentScreen, setErrorMessage]);

	const handleTryNowClick = useCallback(() => {
		onTryNowClick?.();
		if (canActivate) {
			setCurrentScreen('provisioning');
			return;
		}

		const [hasRequested, requestedTrialData] = hasRequestedTrial(cloudId);
		if (hasRequested) {
			setApprovalUrl(requestedTrialData.link);
			onRequestSubmitted?.({ approvalUrl: requestedTrialData.link });
			setCurrentScreen('requested-trial-screen');
			return;
		}

		sendRequestTrial(
			cloudId,
			modalState.edition,
			formatMessage(confirmationMessages.requestTrialComment),
		)
			.then((response) => {
				if (response?.approvalUrl) {
					saveRequestedTrialToLocalStorage(cloudId, response.approvalUrl);
					setApprovalUrl(response.approvalUrl);
				}
				onRequestSubmitted?.({ approvalUrl: response?.approvalUrl });
				setCurrentScreen('requested-trial-screen');
			})
			.catch((error) => {
				setErrorMessage(
					`Failed to send request trial: ${error instanceof Error ? error.message : 'Unknown error'}`,
				);
				setCurrentScreen('error');
			});
	}, [
		setCurrentScreen,
		setErrorMessage,
		onRequestSubmitted,
		onTryNowClick,
		modalState.edition,
		cloudId,
		canActivate,
		formatMessage,
	]);

	const handleProvisioningComplete = useCallback(
		(redirectUrl: string | undefined) => {
			if (redirectUrl) {
				setRedirectUrl(redirectUrl);
			}
			if (onShouldSkipWelcomeScreen?.() && redirectUrl) {
				const { origin } = new URL(redirectUrl);
				setRedirectUrl(
					`${origin}/wiki/welcome?openModal=aifc-onboarding&entryPoint=twc-xflow-switcher`,
				);
				// TODO(STARB-5395): Clean up this reset used to bypass the post-normalization animation.
				// https://hello.jira.atlassian.cloud/jira/browse/STARB-5395
				setWelcomeScreen('welcome');
				setCurrentScreen('normalize-seat');
				return;
			}
			const selectedWelcomeScreen = onGetWelcomeScreen?.() ?? 'welcome';
			setWelcomeScreen(selectedWelcomeScreen);

			// move on to default welcome screen when not the welcome-screen-twcg-animation
			if (selectedWelcomeScreen !== 'welcome-screen-twcg-animation') {
				setCurrentScreen('welcome');
				return;
			}

			if (!isNormalizeSeatModalEnabled() || skipNthUserScreen) {
				setCurrentScreen('welcome-screen-twcg-animation');
			} else {
				setCurrentScreen('normalize-seat');
			}
		},
		[
			onGetWelcomeScreen,
			onShouldSkipWelcomeScreen,
			setCurrentScreen,
			setRedirectUrl,
			skipNthUserScreen,
		],
	);

	const finishProvisioning = useCallback(() => {
		const callback =
			welcomeScreen === 'welcome-screen-twcg-animation'
				? (onWelcomeScreenTwcgAnimationComplete ?? onComplete)
				: onComplete;
		if (callback) {
			callback({
				parsedUrl: parseOnboardingUrl(redirectUrl),
				rawUrl: redirectUrl,
				success: true,
			});
		} else {
			window.location.href = redirectUrl;
		}
	}, [onComplete, onWelcomeScreenTwcgAnimationComplete, redirectUrl, welcomeScreen]);

	const handleWelcomeComplete = useCallback(() => {
		if (isNormalizeSeatModalEnabled()) {
			setCurrentScreen('normalize-seat');
		} else {
			finishProvisioning();
		}
	}, [setCurrentScreen, finishProvisioning]);

	const handleMeetingRecordingsClick = useCallback(() => {
		openLoomMeetingRecordings({
			bridge: 'loomMeetingRecordings',
			cloudId,
			interactionSource: 'confluence',
			loomSource: sourceContext,
		});
		createAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'twcXflowModalSetupMeetingRecordings',
			source: 'twcXflowModalWelcomeScreen',
			attributes: {
				sourceComponent,
				sourceContext,
			},
		}).fire('growth');
		onClose();
	}, [cloudId, createAnalyticsEvent, onClose, sourceComponent, sourceContext]);

	const handleOnClose = useCallback(
		(e?: KeyboardOrMouseEvent) => {
			const shouldAllowClose =
				currentScreen === 'error' ||
				currentScreen === 'confirmation' ||
				currentScreen === 'loading' ||
				currentScreen === 'requested-trial-screen' ||
				currentScreen === 'timeout';
			createAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'twcXflowModalClose',
				attributes: {
					currentScreen,
					sourceComponent,
					sourceContext,
				},
			}).fire('growth');
			e?.stopPropagation?.();
			if (shouldAllowClose) {
				onClose();
			}
		},
		[currentScreen, onClose, createAnalyticsEvent, sourceComponent, sourceContext],
	);

	const handleNormalizeComplete = useCallback(async () => {
		createAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'twcXflowModalNormalizeSeatConfirm',
			source: 'twcXflowModalNormalizeSeatScreen',
			attributes: {
				isNormalizeSeatsEnabled,
				edition: modalState.edition,
				hasPaymentDetails: modalState.hasPaymentDetails,
				userProducts: modalState.userProducts,
				sourceComponent,
				sourceContext,
			},
		}).fire('growth');

		if (isNormalizeSeatsEnabled) {
			try {
				const result = await normalizeSeat({ cloudId });
				createAnalyticsEvent({
					eventType: 'track',
					action: result.started ? 'normalizeSeatStarted' : 'normalizeSeatNotStarted',
					actionSubject: 'normalizeSeatJob',
					actionSubjectId: 'twcXflowModal',
					attributes: {
						sourceComponent,
						sourceContext,
						...(!result.started && { failureReason: 'not_started' }),
					},
				}).fire('growth');
			} catch (error) {
				createAnalyticsEvent({
					eventType: 'track',
					action: 'normalizeSeatStartFailed',
					actionSubject: 'normalizeSeatJob',
					actionSubjectId: 'twcXflowModal',
					attributes: {
						sourceComponent,
						sourceContext,
						failureReason:
							error instanceof NormalizeSeatStartError ? error.failureReason : 'request_error',
						...(error instanceof NormalizeSeatStartError && error.httpStatus !== undefined
							? { httpStatus: error.httpStatus }
							: {}),
					},
				}).fire('growth');
			}
		}
		if (welcomeScreen === 'welcome-screen-twcg-animation') {
			setCurrentScreen('welcome-screen-twcg-animation');
		} else {
			finishProvisioning();
		}
	}, [
		isNormalizeSeatsEnabled,
		cloudId,
		createAnalyticsEvent,
		modalState,
		finishProvisioning,
		setCurrentScreen,
		sourceComponent,
		sourceContext,
		welcomeScreen,
	]);

	// Keep confirmation screen selection in one place.
	const evaluateConfirmationScreenExposures = () => {
		if (sourceContext === SOURCE_CONTEXT.ATLASSIAN_SWITCHER_LOOM_INTERCEPT) {
			const loomCtaText = canActivate
				? modalState.hasPaymentDetails
					? confirmationMessagesLoom.ctaWithPaymentDetailsOnFile
					: confirmationMessagesLoom.ctaNoPaymentDetailsOnFile
				: confirmationMessagesLoom.ctaRequestTrialText;
			return (
				<ConfirmationScreenLoom
					onTryNowClick={handleTryNowClick}
					onClose={handleOnClose}
					canActivate={canActivate}
					edition={modalState.edition}
					hasPaymentDetails={modalState.hasPaymentDetails} // if productionised, lets move this inside the component as in "ConfirmationScreen"
					confirmationSubheader={confirmationMessagesLoom.trialText}
					confirmationCtaText={loomCtaText}
				/>
			);
		}

		return (
			<ConfirmationScreen
				onTryNowClick={handleTryNowClick}
				onClose={handleOnClose}
				canActivate={canActivate}
				edition={modalState.edition}
				hasPaymentDetails={modalState.hasPaymentDetails}
			/>
		);
	};

	const renderScreen = () => {
		switch (currentScreen) {
			case 'loading':
				return <LoadingScreen setApprovalUrl={setApprovalUrl} />;
			case 'error':
				return <ErrorScreen onClose={handleOnClose} />;
			case 'confirmation':
				return evaluateConfirmationScreenExposures();
			case 'provisioning':
				return <ProvisioningScreen onProvisioningComplete={handleProvisioningComplete} />;
			case 'welcome':
				return (
					<WelcomeScreen
						onComplete={handleWelcomeComplete}
						onMeetingRecordingsClick={handleMeetingRecordingsClick}
						showMeetingRecordingsCta={showMeetingRecordingsCta}
					/>
				);
			case 'welcome-screen-twcg-animation':
				return <WelcomeScreenTwcgAnimation onComplete={finishProvisioning} />;
			case 'normalize-seat':
				return <NormalizeSeatScreen onConfirm={handleNormalizeComplete} testId={testId} />;
			case 'requested-trial-screen':
				return <RequestedTrialScreen onClose={handleOnClose} approvalUrl={approvalUrl} />;
			case 'timeout':
				return <TimeoutScreen onClose={handleOnClose} />;
		}
	};

	return (
		<ModalTransition>
			<Modal onClose={handleOnClose} testId={testId} width="x-large">
				<Box
					role="none"
					xcss={styles.modalWrapper}
					onClick={(event: MouseEvent<HTMLDivElement>) => {
						if (currentScreen === 'welcome-screen-twcg-animation') {
							// Keep animation clicks away from the switcher's outside-click listener.
							event.stopPropagation();
						}
					}}
				>
					<ErrorBoundary
						FallbackComponent={() => <ErrorScreen onClose={handleOnClose} />}
						onError={(error: unknown, _info: ErrorInfo) => {
							const message = error instanceof Error ? error.message : String(error);
							setErrorMessage(`Unhandled error: ${message}`);
							captureException(
								'Unhandled error in TWC XFlow modal',
								error instanceof Error ? error : new Error(message),
								{
									cloudId,
									packageName: '@atlassian/twc-xflow-modal',
									teamName: 'starburst',
								},
							);
						}}
					>
						{renderScreen()}
					</ErrorBoundary>
				</Box>
			</Modal>
		</ModalTransition>
	);
};

const getInitialScreen = (prefetch: PrefetchedPlanData, autoStart?: boolean): ScreenState =>
	autoStart && prefetch.activationCapability === 'can-activate' ? 'provisioning' : 'confirmation';

/** Build modal presets from prefetched plan data and an optional initial-screen override. */
const buildModalPresets = (
	prefetch?: PrefetchedPlanData,
	autoStart?: boolean,
	initialScreen?: ScreenState,
) =>
	prefetch
		? {
				initialScreen: initialScreen ?? getInitialScreen(prefetch, autoStart),
				initialModalState: {
					activationCapability: prefetch.activationCapability,
					edition: prefetch.edition,
					hasPaymentDetails: prefetch.hasPaymentDetails,
					status: prefetch.status,
				},
			}
		: initialScreen
			? { initialScreen }
			: undefined;

const useModalStateWithPresets = (
	cloudId: string,
	sourceComponent: string,
	sourceContext: string,
	locale: string,
	orgId?: string,
	prefetch?: PrefetchedPlanData,
	autoStart?: boolean,
	initialScreen?: ScreenState,
	requestedEditionId?: Edition,
	attribution?: Pick<Props, 'touchpointName' | 'upgradeSourceProduct'>,
): ModalContextValue => {
	const presets = useMemo(
		() => buildModalPresets(prefetch, autoStart, initialScreen),
		[prefetch, autoStart, initialScreen],
	);
	return useModalState(
		cloudId,
		sourceComponent,
		sourceContext,
		locale,
		orgId,
		presets,
		autoStart,
		requestedEditionId,
		attribution,
	);
};

const TwcXflowModalWithoutMessages = ({
	autoStart,
	cloudId,
	initialScreen,
	sourceComponent,
	sourceContext,
	onClose,
	onComplete,
	onGetWelcomeScreen,
	onWelcomeScreenTwcgAnimationComplete,
	onRequestSubmitted,
	onShouldSkipWelcomeScreen,
	onTryNowClick,
	testId,
	welcomeScreen,
	orgId,
	prefetch,
	requestedEditionId,
	skipNthUserScreen,
	touchpointName,
	upgradeSourceProduct,
}: Props) => {
	const contextValue = useModalStateWithPresets(
		cloudId,
		sourceComponent,
		sourceContext,
		'en',
		orgId,
		prefetch,
		autoStart,
		initialScreen,
		requestedEditionId,
		{ touchpointName, upgradeSourceProduct },
	);

	return (
		<ModalProvider value={contextValue}>
			<IntlProvider locale={'en'}>
				<TwcXflowModal
					onClose={onClose}
					onComplete={onComplete}
					onGetWelcomeScreen={onGetWelcomeScreen}
					onWelcomeScreenTwcgAnimationComplete={onWelcomeScreenTwcgAnimationComplete}
					onRequestSubmitted={onRequestSubmitted}
					onShouldSkipWelcomeScreen={onShouldSkipWelcomeScreen}
					onTryNowClick={onTryNowClick}
					skipNthUserScreen={skipNthUserScreen}
					testId={testId}
					welcomeScreen={welcomeScreen}
				/>
			</IntlProvider>
		</ModalProvider>
	);
};

const TwcXflowModalWithMessages = ({
	autoStart,
	locale: requestedLocale = 'en',
	cloudId,
	initialScreen,
	sourceComponent,
	sourceContext,
	onClose,
	onComplete,
	onGetWelcomeScreen,
	onWelcomeScreenTwcgAnimationComplete,
	onRequestSubmitted,
	onShouldSkipWelcomeScreen,
	onTryNowClick,
	testId,
	welcomeScreen,
	orgId,
	prefetch,
	requestedEditionId,
	skipNthUserScreen,
	touchpointName,
	upgradeSourceProduct,
}: Props) => {
	const { isLoading, locale, messages } = useMessages(requestedLocale);
	const contextValue = useModalStateWithPresets(
		cloudId,
		sourceComponent,
		sourceContext,
		locale,
		orgId,
		prefetch,
		autoStart,
		initialScreen,
		requestedEditionId,
		{ touchpointName, upgradeSourceProduct },
	);

	if (isLoading) {
		return null;
	}

	return (
		<ModalProvider value={contextValue}>
			<IntlProvider locale={locale} messages={messages}>
				<TwcXflowModal
					onClose={onClose}
					onComplete={onComplete}
					onGetWelcomeScreen={onGetWelcomeScreen}
					onWelcomeScreenTwcgAnimationComplete={onWelcomeScreenTwcgAnimationComplete}
					onRequestSubmitted={onRequestSubmitted}
					onShouldSkipWelcomeScreen={onShouldSkipWelcomeScreen}
					onTryNowClick={onTryNowClick}
					skipNthUserScreen={skipNthUserScreen}
					testId={testId}
					welcomeScreen={welcomeScreen}
				/>
			</IntlProvider>
		</ModalProvider>
	);
};

const TwcXflowModalDefault = (props: Props): JSX.Element => {
	const { locale = 'en' } = props;

	if (locale === 'en') {
		return <TwcXflowModalWithoutMessages {...props} />;
	}

	return <TwcXflowModalWithMessages {...props} />;
};

export default TwcXflowModalDefault;
